import React, { useEffect, useRef, useState } from 'react';
import { FileText, Volume2, CheckCircle2, Bell, TrendingUp, Check, Mic } from 'lucide-react';
import * as styles from './aiEngineerFellowship.module.scss';
import { ICONS } from './iconRegistry';

const ROTATE_MS = 6000;

const InlineIcon = ({ icon: Icon, size = 12 }) => (
  <Icon size={size} className="mr-1 inline -mt-0.5" aria-hidden="true" />
);

// These interactive mockups (chat bubbles, progress bars, a terminal diff, a
// waveform) are UI, not editable copy — unlike everything else on this page,
// they stay hardcoded here rather than in the YAML, keyed by each app's `id`
// so they still line up with the YAML-sourced app list.
const WINDOWS = {
  askdocs: (
    <>
      <div className={styles.bub + ' ' + styles.me}>What&apos;s our refund window for annual plans?</div>
      <div className={styles.bub}>
        14 days from the invoice date for annual plans; 7 days for monthly. Refunds go back to
        the original payment method.
        <br />
        <span className={styles.cite}>
          <InlineIcon icon={FileText} /> Billing-Policy.md
        </span>
        <span className={styles.cite}>
          <InlineIcon icon={FileText} /> Refunds-FAQ
        </span>
      </div>
      <div>
        <span className={styles.badgeEv}>RAGAS faithfulness 0.92</span>
        <span className={styles.badgeEv}>p95 1.4 s</span>
        <span className={styles.badgeEv}>₹0.21 / query</span>
      </div>
    </>
  ),
  triageops: (
    <>
      <div>
        <span className={styles.tagS}>Classify</span> →{' '}
        <span className={styles.tagS}>Draft grounded reply</span> →{' '}
        <span className={styles.tagS}>Route / escalate</span>
      </div>
      <div className={styles.tk}>
        <span className={styles.id}>#4821</span>
        <span className={styles.t}>Login fails after SSO change</span>
        <span className={styles.tagS}>Auth</span>
        <span className={styles.tagS}>Draft ready</span>
        <span className={styles.tagS}>→ L2</span>
      </div>
      <div className={styles.tk}>
        <span className={styles.id}>#4822</span>
        <span className={styles.t}>Refund not received</span>
        <span className={styles.tagS}>Billing</span>
        <span className={styles.tagS}>Draft ready</span>
        <span className={styles.tagS}>→ Auto-reply</span>
      </div>
      <div className={styles.tk}>
        <span className={styles.id}>#4823</span>
        <span className={styles.t}>&quot;Legal threat&quot; detected in message</span>
        <span className={`${styles.tagS} ${styles.y}`}>Escalate → human</span>
      </div>
      <div>
        <span className={styles.badgeEv}>Agent evals 46/50 pass</span>
        <span className={styles.badgeEv}>HITL on 3 categories</span>
        <span className={styles.badgeEv}>MCP: helpdesk + chat</span>
      </div>
    </>
  ),
  domaincopilot: (
    <>
      <div style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>
        Domain eval — fintech policy Q&amp;A (golden set, 200 items)
      </div>
      <div className={styles.barrow}>
        <span className={styles.lbl}>Base model</span>
        <div className={styles.trk}>
          <div className={styles.fil} style={{ width: '58%', background: '#B9CFCA' }}></div>
        </div>
        <span className={styles.val}>58%</span>
      </div>
      <div className={styles.barrow}>
        <span className={styles.lbl}>Base + RAG</span>
        <div className={styles.trk}>
          <div className={styles.fil} style={{ width: '74%', background: '#7BD7C0' }}></div>
        </div>
        <span className={styles.val}>74%</span>
      </div>
      <div className={styles.barrow}>
        <span className={styles.lbl}>Fine-tuned + RAG</span>
        <div className={styles.trk}>
          <div className={styles.fil} style={{ width: '86%' }}></div>
        </div>
        <span className={styles.val}>86%</span>
      </div>
      <div>
        <span className={styles.badgeEv}>LoRA · 4-bit</span>
        <span className={styles.badgeEv}>−70% cost vs frontier</span>
        <span className={styles.badgeEv}>Published to Model hub</span>
      </div>
    </>
  ),
  voicemate: (
    <>
      <div className={styles.wave}>
        {Array.from({ length: 16 }).map((_, i) => (
          <i key={i} style={{ animationDelay: `-${(i * 0.12).toFixed(2)}s` }}></i>
        ))}
      </div>
      <div className={styles.bub + ' ' + styles.me}>
        <InlineIcon icon={Mic} /> &quot;Hi, where&apos;s my order?&quot;
      </div>
      <div className={styles.bub}>
        <InlineIcon icon={Volume2} /> &quot;Order #1183 shipped this morning — it arrives
        tomorrow. Want the tracking link by SMS?&quot;
      </div>
      <div>
        <span className={styles.badgeEv}>~700 ms round-trip</span>
        <span className={styles.badgeEv}>Tool call: orders.lookup</span>
        <span className={styles.badgeEv}>Barge-in supported</span>
      </div>
    </>
  ),
  shipit: (
    <>
      <div className={styles.metrics}>
        <div>
          <b>820 ms</b>
          <small>p95 latency</small>
        </div>
        <div>
          <b>₹0.38</b>
          <small>cost / query</small>
        </div>
        <div>
          <b>99.9%</b>
          <small>uptime · 30 days</small>
        </div>
      </div>
      <div className={styles.tk}>
        <span className={styles.t}>
          <InlineIcon icon={CheckCircle2} /> CI/CD · main → prod (Bedrock + SageMaker)
        </span>
        <span className={styles.tagS}>deployed 2 min ago</span>
      </div>
      <div className={styles.tk}>
        <span className={styles.t}>
          <InlineIcon icon={Bell} /> Cost alarm at 80% of monthly budget
        </span>
        <span className={styles.tagS}>armed</span>
      </div>
      <div className={styles.tk}>
        <span className={styles.t}>
          <InlineIcon icon={TrendingUp} /> Traces → Langfuse · metrics → Grafana
        </span>
        <span className={styles.tagS}>live</span>
      </div>
    </>
  ),
  repoagent: (
    <>
      <div className={styles.tk}>
        <span className={styles.id}>PR #2147</span>
        <span className={styles.t}>
          <b>feat:</b> add per-tenant rate-limit middleware
        </span>
        <span className={styles.tagS}>12 files · +418 −56</span>
      </div>
      <div className={styles.prDiff}>
        <span className={styles.add}>+ if limiter.exceeded(tenant_id):</span>
        <br />
        <span className={styles.add}>+ raise RateLimited(retry_after=limiter.reset_in())</span>
        <br />
        <span className={styles.del}>- return handler(request)</span>
        <br />
        <span className={styles.add}>+ return handler(request, tenant=tenant_id)</span>
      </div>
      <div>
        <span className={styles.badgeEv}>
          <InlineIcon icon={Check} /> evals 47/47
        </span>
        <span className={styles.badgeEv}>
          <InlineIcon icon={Check} /> agentic review
        </span>
        <span className={styles.badgeEv}>
          <InlineIcon icon={Check} /> CI green
        </span>
        <span className={styles.badgeEv}>spec → plan → build → review</span>
      </div>
    </>
  ),
  capstone: (
    <>
      <div className={styles.spec}>
        <div>One-page spec</div>
        <div>Success metrics</div>
        <div>PoC → MVP → production</div>
        <div>Deployed on AWS</div>
        <div>Evals + monitoring</div>
        <div>Demo-day story</div>
      </div>
      <div>
        <span className={styles.badgeEv}>Deep-research assistant with citations</span>
        <span className={styles.badgeEv}>CRM record enrichment</span>
        <span className={styles.badgeEv}>Payments reconciliation &amp; anomaly triage</span>
        <span className={styles.badgeEv}>API docs &amp; test-suite generator</span>
        <span className={styles.badgeEv}>Delivery-risk agent over an issue tracker</span>
        <span className={styles.badgeEv}>…or bring your own</span>
      </div>
    </>
  ),
};

const DeployedShowcase = ({ apps }) => {
  const [active, setActive] = useState(0);
  const [manual, setManual] = useState(false);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (manual || paused) return undefined;
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % apps.length);
    }, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [manual, paused, active, apps.length]);

  const handleSelect = (index) => {
    setActive(index);
    setManual(true);
  };

  const activeApp = apps[active];

  return (
    <section className={`${styles.mint}`} id="deployed" style={{ padding: '48px 0' }}>
      <div className={styles.wrap}>
        <div className={styles.shead} style={{ marginBottom: 26 }}>
          <span className={styles.eyebrow}>You graduate having deployed</span>
          <h2 style={{ fontSize: 28 }}>6 Guided Production Projects — Plus Your Own Capstone</h2>
          <p>
            Click an app to see what you&apos;ll ship. Each one is deployed, evaluated and
            monitored — with a one-page spec, success metrics and a demo.
          </p>
        </div>

        <div
          className={`${styles.show} ${paused ? styles.paused : ''} ${manual ? styles.manual : ''}`}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className={styles.showList} ref={listRef} role="tablist" aria-label="Apps you will deploy">
            {apps.map((app, index) => {
              const Icon = ICONS[app.icon];
              return (
                <button
                  key={app.id}
                  type="button"
                  role="tab"
                  aria-selected={index === active}
                  className={`${styles.appRow} ${index === active ? styles.on : ''} ${
                    app.isCap ? styles.isCap : ''
                  }`}
                  onClick={() => handleSelect(index)}
                >
                  <span className={styles.ic}>{Icon && <Icon size={20} aria-hidden="true" />}</span>
                  <span className={styles.tx}>
                    <b>{app.title}</b>
                    <span>{app.blurb}</span>
                  </span>
                  <span className={styles.m}>{app.tag}</span>
                  <i className={styles.bar}></i>
                </button>
              );
            })}
          </div>

          <div className={styles.showPanel} role="tabpanel">
            <div className={styles.win}>
              <div className={styles.barTop}>
                <span className={styles.dot}></span>
                <span className={styles.dot}></span>
                <span className={styles.dot}></span>
                <span className={styles.url}>
                  {activeApp.id} · {activeApp.isCap ? 'demo day' : 'deployed on AWS'}
                </span>
              </div>
              <div className={styles.body}>{WINDOWS[activeApp.id]}</div>
            </div>
            <div>
              <h3>{activeApp.title}</h3>
              <div className={styles.role}>{activeApp.role}</div>
            </div>
            <p>{activeApp.description}</p>
            <div className={styles.meta}>
              {activeApp.meta.map((m) => (
                <span key={m} className={`${styles.chip} ${styles.g}`}>
                  {m}
                </span>
              ))}
            </div>
            <div className={styles.ships}>
              <b>Ships as:</b> {activeApp.ships.replace('Ships as: ', '')}
            </div>
          </div>
        </div>
        <p className={styles.showCap}>Sample screens — you build, deploy and operate each one yourself.</p>
      </div>
    </section>
  );
};

export default DeployedShowcase;
